import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Briefcase, ChevronLeft, ChevronRight, Clock, DoorOpen, MapPin, ArrowRight } from 'lucide-react';
import { format, parseISO, endOfWeek, startOfWeek, addWeeks, subWeeks, getISOWeek } from 'date-fns';
import { de } from 'date-fns/locale';

export const MyShiftsWidget = ({ myShifts, shiftsWeek, setShiftsWeek, shiftsLoading }) => {
  const navigate = useNavigate();

  return (
    <Card className="border-border/60">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
          <div className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-primary flex-shrink-0" />
            <CardTitle className="text-lg">Meine Einsätze</CardTitle>
          </div>
          <div className="flex items-center justify-between sm:justify-end gap-1 sm:gap-2 w-full sm:w-auto">
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShiftsWeek(subWeeks(shiftsWeek, 1))}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium min-w-[60px] sm:min-w-[80px] text-center">KW {getISOWeek(shiftsWeek)}</span>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShiftsWeek(addWeeks(shiftsWeek, 1))}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <Button variant="outline" size="sm" className="h-8 px-3 flex-shrink-0" onClick={() => setShiftsWeek(startOfWeek(new Date(), { weekStartsOn: 1 }))}>
              Heute
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-2 sm:mt-1">
          {format(shiftsWeek, 'dd.MM.', { locale: de })} - {format(endOfWeek(shiftsWeek, { weekStartsOn: 1 }), 'dd.MM.yyyy', { locale: de })}
        </p>
      </CardHeader>
      <CardContent>
        {shiftsLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : myShifts.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <Briefcase className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p>Keine Einsätze in dieser Woche</p>
          </div>
        ) : (
          <div className="space-y-2">
            {myShifts.map((shift) => (
              <div key={shift.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                <div className="w-1.5 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: shift.massnahme_color || '#6B7280' }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{format(parseISO(shift.date), 'EEEE, dd.MM.', { locale: de })}</span>
                    {shift.massnahme_short_code && (
                      <Badge variant="outline" className="text-xs" style={{ borderColor: shift.massnahme_color, color: shift.massnahme_color }}>
                        {shift.massnahme_short_code}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground mt-0.5">
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{shift.start_time && shift.end_time ? `${shift.start_time} - ${shift.end_time}` : 'Ganztags'}</span>
                    {shift.room_name && (
                      <span className="flex items-center gap-1">
                        <DoorOpen className="h-3 w-3" />
                        {shift.room_number ? `${shift.room_number} - ${shift.room_name}` : shift.room_name}
                      </span>
                    )}
                    {shift.massnahme_name && <span className="truncate">{shift.massnahme_name}</span>}
                  </div>
                </div>
                {shift.location_name && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3" />{shift.location_name}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="mt-4 pt-4 border-t">
          <Button variant="outline" className="w-full" onClick={() => navigate('/shifts')}>
            Zur Einsatzplanung
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
